import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { publicApi } from '../api/api';
import razorpayUtils from '../utils/razorpayUtils';
import { fetchCashfreeEligiblePaymentMethods } from '../utils/cashfreeUtils';

const { loadRazorpaySDK } = razorpayUtils;

const DEFAULT_PAYMENT_METHODS = {
  upi:        { enabled: true,  apps: ['gpay', 'phonepe', 'paytm', 'bhim'] },
  card:       { enabled: true,  types: ['credit', 'debit'] },
  netbanking: { enabled: true,  banks: [] },
  wallet:     { enabled: true,  wallets: [] },
};

// Populated when activeGateway === 'cashfree' — see fetchCashfreeEligiblePaymentMethods's
// doc comment (cashfreeUtils.js) for why this replaces a static list.
const DEFAULT_CASHFREE_PAYMENT_METHODS = { netbanking: [], wallet: [] };

/**
 * Parses the methods object from the Razorpay SDK ready event.
 * netbanking: { HDFC: 'HDFC Bank', ICIC: 'ICICI Bank', ... }
 * wallet:     { payzapp: true, mobikwik: false, ... }
 */
function parseRazorpayMethods(methods) {
  if (!methods || typeof methods !== 'object') return DEFAULT_PAYMENT_METHODS;

  const netbankingBanks = methods.netbanking && typeof methods.netbanking === 'object'
    ? Object.keys(methods.netbanking)
    : [];

  const enabledWallets = methods.wallet && typeof methods.wallet === 'object'
    ? Object.keys(methods.wallet).filter((k) => methods.wallet[k] === true)
    : [];

  return {
    upi:        { enabled: !!(methods.upi),  apps: ['gpay', 'phonepe', 'paytm', 'bhim'] },
    card:       { enabled: !!(methods.card || methods.credit_card || methods.debit_card), types: ['credit', 'debit'] },
    netbanking: { enabled: netbankingBanks.length > 0, banks: netbankingBanks },
    wallet:     { enabled: enabledWallets.length > 0,  wallets: enabledWallets },
  };
}

const DEFAULT_CONFIG = {
  checkoutMode: 'standard',
  razorpayKeyId: null,
  // 'razorpay' | 'cashfree' — which gateway strategy CheckoutPage.jsx/etc.
  // dispatch to. Note: the AUTHORITATIVE source for a given payment is the
  // `gateway` field on that payment's own order object (set at order-creation
  // time, e.g. via /wallet/initiate) — not this context value — so a mid-flow
  // admin toggle can't strand an in-progress checkout on the wrong strategy.
  // This context value is only a fallback / used for pre-order-creation UI.
  activeGateway: 'razorpay',
  paymentMethods: DEFAULT_PAYMENT_METHODS,
  cashfreePaymentMethods: DEFAULT_CASHFREE_PAYMENT_METHODS,
  // false until the live Cashfree eligibility fetch has finished (success OR
  // failure) — lets the UI tell "still loading" from "loaded, nothing enabled".
  cashfreeMethodsLoaded: false,
  ensureCashfreeMethods: () => {},
};

export const CheckoutContext = createContext(DEFAULT_CONFIG);

export function CheckoutProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);
  const cashfreeFetchRef = useRef({ started: false, amount: undefined, seq: 0 });

  // Fetch Cashfree's live eligibility list. Called by the config load below when
  // the GLOBAL active gateway is cashfree (no amount known yet), and by
  // CheckoutPage when THIS order's gateway is cashfree (the two can differ — an
  // order's gateway is fixed at creation, the global toggle can flip afterwards).
  //
  // Eligibility can depend on the order amount, so CheckoutPage passes it: the
  // list is refetched whenever a different real amount arrives (same amount =
  // cached, an amount-less call never refetches once something is started).
  // `seq` makes only the latest request win, so a slow amount-less response can't
  // overwrite the amount-specific list.
  const ensureCashfreeMethods = useCallback((amount) => {
    const amt = Number(amount) > 0 ? Number(amount) : undefined;
    const state = cashfreeFetchRef.current;
    if (state.started && (amt === undefined || amt === state.amount)) return;
    state.started = true;
    state.amount = amt;
    const seq = ++state.seq;
    if (amt !== undefined) {
      // Drop the previous list so a bank ineligible for this amount is never shown meanwhile.
      setConfig((prev) => ({ ...prev, cashfreePaymentMethods: DEFAULT_CASHFREE_PAYMENT_METHODS, cashfreeMethodsLoaded: false }));
    }
    fetchCashfreeEligiblePaymentMethods(amt).then((cashfreePaymentMethods) => {
      if (seq !== state.seq) return;
      setConfig((prev) => ({ ...prev, cashfreePaymentMethods, cashfreeMethodsLoaded: true }));
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    publicApi
      .get('/checkout-config')
      .then(async (res) => {
        if (cancelled || !res.data) return;

        const checkoutMode = res.data.checkoutMode || 'standard';
        const razorpayKeyId = res.data.razorpayKeyId || null;
        const activeGateway = res.data.activeGateway || 'razorpay';

        // Set config immediately with defaults so the UI isn’t blocked
        setConfig((prev) => ({
          ...prev,
          checkoutMode,
          razorpayKeyId,
          activeGateway,
          paymentMethods: DEFAULT_PAYMENT_METHODS,
        }));
        setConfigLoading(false);

        // Cashfree: fetch its own live-eligibility list (no SDK-ready-event
        // equivalent exists for it — see fetchCashfreeEligiblePaymentMethods's
        // doc comment).
        if (activeGateway === 'cashfree') {
          ensureCashfreeMethods();
        }

        // For custom checkout: use the SDK ready event to get live enabled methods.
        // new Razorpay({ key }) fires once('ready', fn) with response.methods—no REST call needed.
        if (checkoutMode === 'custom' && razorpayKeyId) {
          try {
            const sdkLoaded = await loadRazorpaySDK('custom');
            if (!sdkLoaded || !window.Razorpay || cancelled) return;

            const rzp = new window.Razorpay({ key: razorpayKeyId });
            rzp.once('ready', (response) => {
              if (cancelled) return;
              const parsed = parseRazorpayMethods(response?.methods);
              setConfig((prev) => ({ ...prev, paymentMethods: parsed }));
            });
          } catch (_) {
            // Non-critical — DEFAULT_PAYMENT_METHODS already applied
          }
        }
      })
      .catch(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => { cancelled = true; };
  }, [ensureCashfreeMethods]);

  return (
    <CheckoutContext.Provider value={{ ...config, configLoading, ensureCashfreeMethods }}>
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckoutConfig() {
  return useContext(CheckoutContext);
}
